import { ChakraProvider, ColorModeScript } from "@chakra-ui/react";
import { useEffect } from "react";
import { CookiesProvider } from "react-cookie";
import { I18nextProvider } from "react-i18next";
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";

import { i18next } from "./i18n";
import { IS_LOCALHOST } from "./setupApiClient";
import "./style.css";
import theme from "./theme";

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        // suppressHydrationWarning: <html>/<body> attributes are legitimately
        // mutated before hydration (ColorModeScript sets the color-mode class,
        // browser extensions inject data-* attributes).
        <html lang={i18next.language} suppressHydrationWarning>
            <head>
                <meta charSet="utf-8" />
                <link rel="icon" href="/favicon.ico" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />

                <link rel="apple-touch-icon" sizes="120x120" href="/apple-touch-icon.png" />
                <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
                <link rel="manifest" href="/site.webmanifest" />
                <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
                <meta name="msapplication-TileColor" content="#da532c" />
                <meta name="theme-color" content="#ffffff" />
                <ColorModeScript initialColorMode={theme.config.initialColorMode} />

                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Fredoka:wght@300..700&family=Nunito:ital,wght@0,200..1000;1,200..1000&display=swap"
                    rel="stylesheet"
                />
                <title>Garrigue Games</title>
                {!IS_LOCALHOST && (
                    <script
                        defer
                        src="https://stats.mlejnek.cafour.cz/do.js"
                        data-website-id="96cdfdc7-6738-47e6-b7f7-2d26cc609e87"
                    />
                )}

                <Meta />
                <Links />
            </head>
            <body suppressHydrationWarning>
                <noscript>You need to enable JavaScript to run this app.</noscript>
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

export default function App() {
    // The server always renders the default language; apply the persisted
    // language only after hydration to avoid a server/client markup mismatch.
    useEffect(() => {
        const storedLanguage = localStorage.getItem("i18nextLng");
        if (storedLanguage && storedLanguage !== i18next.language) {
            i18next.changeLanguage(storedLanguage);
        }
    }, []);

    return (
        <I18nextProvider i18n={i18next}>
            <CookiesProvider>
                <ChakraProvider theme={theme}>
                    <Outlet />
                </ChakraProvider>
            </CookiesProvider>
        </I18nextProvider>
    );
}
